import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Form, Input } from 'antd';
import ModalForm from 'components/ModalForm';
import QueryBuilderInput from 'components/QueryBuilder/QueryBuilderInput';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import useCustomer from 'hooks/useCustomer';
import pDebounce from 'p-debounce';
import { getBaseQuery, getDateQuery } from 'shared/utils/queryBuilder';
import { getDefaultDateRange } from 'shared/constants/date';
import TableResult from '../CustomResults/TableResult';

const SEGMENT_QUERY = gql`
  query($id: ID!) {
    segment(id: $id) {
      id
      title
      query
    }
  }
`;

const CREATE_MUTATION = gql`  
  mutation CreateSegment($input: SegmentInput!) {
    createSegment(input: $input) {
      id
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation UpdateSegment($id: ID!, $input: SegmentInput!) {
    updateSegment(id: $id, input: $input) {
      id
    }
  }
`;

const RESULT_QUERY = gql`
  query SegmentEditorResults($input: ResultsInput!) {
    results(input: $input) {
      segmentId
      data
    }
  }
`;

const queryValidator = async (_, value) => {
  if (!value?.rules?.length) {
    throw new Error('Query is empty');
  }

  for (const rule of value.rules) {
    if (!rule.field) {
      throw new Error('Query rule without field');
    }

    if (['in', 'notIn', 'between', 'notBetween', 'contains', 'doesNotContain', '=', '!=', '>', '<', '>=', '<='].includes(rule.operator) && (rule.value == null || rule.value === '')) {
      throw new Error('Query rule without value');
    }
  }
};

const metrics = [{ name: 'visits', field: 'visitId' }, { name: 'surveyResponses', field: 'surveyResponseId' }];

const getQueryVariables = async (customerId, query) => {
  if (customerId) {
    try {
      await queryValidator(null, query);

      const baseQuery = getBaseQuery({ customerId });

      return {
        input: {
          baseQuery,
          metrics,
          mainSegment: {
            id: 'main',
            query,
            dateQuery: getDateQuery({ dateRangeConfig: getDefaultDateRange() }),
          },
        },
      };
    } catch (error) {
      return false;
    }
  }
  return false;
};

const SegmentEditor = ({ open, onCancel, onDone, segmentId }) => {
  const { data } = useQuery(SEGMENT_QUERY, { variables: { id: segmentId }, skip: !segmentId });
  const { customerId } = useCustomer();
  const [createSegment] = useMutation(CREATE_MUTATION);
  const [updateSegment] = useMutation(UPDATE_MUTATION);
  const [variables, setVariables] = useState(null);
  const results = useQuery(RESULT_QUERY, { variables, skip: !variables });

  const onSubmit = async (values) => {
    if (segmentId) {
      await updateSegment({ variables: { id: segmentId, input: values } });
    } else {
      await createSegment({ variables: { input: values } });
    }
  };

  const onChange = useCallback(pDebounce(async (values) => {
    const changedVariables = await getQueryVariables(customerId, values.query);
    if (changedVariables) {
      setVariables(changedVariables);
    }
  }, 500), [customerId]);

  useEffect(() => {
    if (open) {
      if (data?.segment?.query) {
        onChange(data.segment);
      } else {
        setVariables(null);
      }
    }
  }, [open, data]);

  return (
    <ModalForm title="Segment Editor" open={open} onCancel={onCancel} onDone={onDone} onSubmit={onSubmit} fullWidth initialValues={data?.segment} formReset={data} onValuesChange={onChange}>
      <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Title is required' }]}>
        <Input />
      </Form.Item>
      <Form.Item label="Query" name="query" rules={[{ validator: queryValidator }]}>
        <QueryBuilderInput customerId={customerId} />
      </Form.Item>
      <Form.Item label="Data matching this segment from the last 30 days">
        {results.error ? (
          <Alert message={results.error.message} type="error" />
        ) : (
          <TableResult data={results.data?.segmentResults} metrics={metrics} loading={results.fetching} />
        )}
      </Form.Item>
    </ModalForm>
  );
};

export default SegmentEditor;
